import React, {useCallback, useEffect, useRef, useState} from "react";
import posthog from "posthog-js";

import {useSeo} from "../hooks/useSeo";
import DrawShapeQuery, {ShapeResultContext} from "./DrawShapeQuery";
import ShapeResultsSummary from "./ShapeResultsSummary";
import StockChartDisplay from "./StockChartDisplay";

export default function DrawShapePage() {
  useSeo({
    title: "Draw a Shape — DejaChart",
    description:
      "Sketch any price shape, upload a chart, or use a real security's price history, and see the historical analogs the DTW+cosine matching engine retrieves for it.",
    path: "/draw",
  });

  const [query, setQuery] = useState<StockChartQuery>({} as StockChartQuery);
  const [matches, setMatches] = useState<MatchesState[]>([]);
  const [forwardDistribution, setForwardDistribution] = useState<ForwardDistribution | null>(null);
  const [forwardCurve, setForwardCurve] = useState<ForwardCurve | null>(null);
  const [resultContext, setResultContext] = useState<ShapeResultContext | null>(null);
  const [fetching, setFetching] = useState<boolean>(false);
  const resultsRef = useRef<HTMLDivElement>(null);

  useEffect(() => { posthog.capture("draw_shape_opened"); }, []);

  const handleSetMatches = useCallback((next: React.SetStateAction<MatchesState[]>) => {
    setMatches(next);
    // setMatches is always called with a plain array (never a functional updater) by
    // DrawShapeQuery, so this branch mirrors StockQueryPage's handleSetMatches — only
    // the plain-array path is ever exercised in practice.
    const resolved = typeof next === "function" ? next([]) : next;
    if (resolved.length > 0 && resultsRef.current) {
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({behavior: "smooth", block: "start"});
      }, 100);
    }
  }, []);

  return (
    <div className="stock-query-page">
      <div className="page-content">
        <DrawShapeQuery
          setQuery={setQuery}
          setMatches={handleSetMatches}
          setForwardDistribution={setForwardDistribution}
          setForwardCurve={setForwardCurve}
          setResultContext={setResultContext}
          onFetchingChange={setFetching}
        />

        <div ref={resultsRef}>
          {/* This page renders the forward-outcome panel itself (percentile band +
              column thinning for the long custom horizons this page can request), so
              the curve/distribution are deliberately NOT handed to StockChartDisplay —
              it would draw a second, differently-configured copy of the same chart. */}
          {!fetching && matches.length > 0 && (
            <ShapeResultsSummary
              context={resultContext}
              forwardCurve={forwardCurve}
              forwardDistribution={forwardDistribution}
            />
          )}

          <StockChartDisplay
            query={query}
            matches={matches}
            loading={fetching}
            forwardDistribution={null}
            forwardCurve={null}
          />
        </div>
      </div>
    </div>
  );
}
